import React, { useCallback, useEffect } from "react";
import { styled } from "@mui/material/styles";
import { Backdrop, CircularProgress, Container } from "@mui/material";
import { Button, Typography } from "@mui/material";
import { useViewport } from "../../helpers";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import ImageList from "../../components/imageList/ImageList";
import { useSnackbar } from "notistack";
import InfiniteScroll from "react-infinite-scroll-component";
import { myExpariencesService } from "../../services";

const PREFIX = "GeolocalisedExpariences";

const classes = {
  updateButtonContainer: `${PREFIX}-updateButtonContainer`,
  pullToRefresh: `${PREFIX}-pullToRefresh`,
  containerMarginTop: `${PREFIX}-containerMarginTop`,
  backdrop: `${PREFIX}-backdrop`,
};

const StyledDiv = styled("div")(({ theme }) => ({
  [`& .${classes.updateButtonContainer}`]: {
    marginBottom: "0.5rem",
    height: "40px",
    display: "flex",
    justifyContent: "flex-end",
  },

  [`& .${classes.pullToRefresh}`]: {
    textAlign: "center",
  },

  [`& .${classes.containerMarginTop}`]: {
    marginTop: "1.5rem",
  },

  [`& .${classes.backdrop}`]: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
}));

const FETCH_LIMIT = 20;

const GeolocalisedExpariences = ({ bought = false }) => {
  const { width } = useViewport();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [expariencesData, setExpariencesData] = React.useState({
    data: null,
    offset: 0,
    limit: FETCH_LIMIT,
    hasMore: false,
  });
  const [fetchingLoader, setFetchingLoader] = React.useState(false);

  const getData = useCallback(
    (myOffset = 0, myLimit = FETCH_LIMIT) => {
      setFetchingLoader(true);
      myExpariencesService
        .getUserPurchasableGeolocs(myOffset, myLimit)
        .then((res) => {
          setExpariencesData(res);
          setFetchingLoader(false);
        })
        .catch((err) => {
          console.log("err >", err);
          enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
        });
    },
    [enqueueSnackbar, t]
  );

  const getBoughtData = useCallback(
    (myOffset = 0, myLimit = FETCH_LIMIT) => {
      setFetchingLoader(true);
      myExpariencesService
        .getUserPurchasedGeolocs(myOffset, myLimit)
        .then((res) => {
          setExpariencesData(res);
          setFetchingLoader(false);
        })
        .catch((err) => {
          console.log("err >", err);
          enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
        });
    },
    [enqueueSnackbar, t]
  );

  useEffect(() => {
    if (bought) getBoughtData();
    else getData();
  }, [getData, getBoughtData, bought]);

  function handleRefreshDesktop() {
    getData();
  }

  const handleRefreshMobile = async () => {
    getData();
  };

  const fetchNextData = () => {
    getData(parseInt(expariencesData.offset) + FETCH_LIMIT, FETCH_LIMIT);
  };

  if ((!bought && !expariencesData.data) || (bought && !expariencesData)) {
    return (
      <Container>
        <div className="loading-container">
          <CircularProgress size={50} />
          <Typography>{t("loaders.fetching")}</Typography>
        </div>
      </Container>
    );
  } else if (
    (!bought && expariencesData.data.length === 0) ||
    (bought && expariencesData.length === 0)
  ) {
    return (
      <Typography
        variant="h6"
        sx={{
          display: "flex",
          justifyContent: "center",
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        }}
      >
        {t("userDashboard.noResults")}
      </Typography>
    );
  } else {
    if ([bought ? expariencesData : expariencesData.data].length >= 0) {
      const { hasMore, data } = expariencesData;
      return (
        <StyledDiv>
          <Container className={classes.containerMarginTop}>
            <Backdrop className={classes.backdrop} open={fetchingLoader}>
              <CircularProgress color="inherit" />
            </Backdrop>
            {width > 768 && hasMore ? (
              <React.Fragment>
                <div className={classes.updateButtonContainer}>
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={() => !fetchingLoader && handleRefreshDesktop()}
                  >
                    {fetchingLoader ? (
                      <CircularProgress size={28} />
                    ) : (
                      t("myExpariences.refresh")
                    )}
                  </Button>
                </div>
                <InfiniteScroll
                  dataLength={[bought ? expariencesData : data].length} //This is important field to render the next data
                  next={() => (!fetchingLoader ? fetchNextData() : undefined)}
                  hasMore={bought ? false : hasMore}
                  loader={
                    <div style={{ height: "100px" }}>
                      <CircularProgress size={50} />
                    </div>
                  }
                >
                  <ImageList
                    myExpariences={bought ? expariencesData : data}
                    redirectUrl={
                      bought ? `/purchased-geoloc/` : `/purchase-geoloc/`
                    }
                  />
                </InfiniteScroll>
              </React.Fragment>
            ) : (
              <div>
                <InfiniteScroll
                  dataLength={[bought ? expariencesData : data].length} //This is important field to render the next data
                  next={() => (!fetchingLoader ? fetchNextData() : undefined)}
                  hasMore={bought ? false : hasMore}
                  loader={
                    <div style={{ height: "100px" }}>
                      <CircularProgress size={50} />
                    </div>
                  }
                  refreshFunction={handleRefreshMobile}
                  pullDownToRefresh
                  pullDownToRefreshThreshold={150}
                  pullDownToRefreshContent={
                    <h3 style={{ textAlign: "center" }}>
                      &#8595; {t("infiniteScroll.pullDownToRefresh")}
                    </h3>
                  }
                  releaseToRefreshContent={
                    <h3 style={{ textAlign: "center" }}>
                      &#8593; {t("infiniteScroll.releaseToRefresh")}
                    </h3>
                  }
                >
                  <ImageList
                    myExpariences={bought ? expariencesData : data}
                    redirectUrlWithId={
                      bought ? `/purchased-geoloc/` : `/purchase-geoloc/`
                    }
                  />
                </InfiniteScroll>
              </div>
            )}
          </Container>
        </StyledDiv>
      );
    } else {
      return (
        <Container>
          <div className="loading-container">
            <Typography>{t("myExpariences.noResults")}</Typography>
          </div>
        </Container>
      );
    }
  }
};

export default GeolocalisedExpariences;
